import React, { useState, useEffect } from 'react';
import { facebookService } from '../services/facebook';

const FACEBOOK_APP_ID = '3211801755664069';

export const FacebookTest: React.FC = () => {
  const [sdkStatus, setSdkStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [appInfo, setAppInfo] = useState<any>(null);
  const [testResults, setTestResults] = useState<string[]>([]);
  const [loginStatus, setLoginStatus] = useState<string>('');

  useEffect(() => {
    initializeFacebook();
  }, []);

  const initializeFacebook = async () => {
    try {
      addTestResult('Initializing Facebook SDK...');
      await facebookService.init(FACEBOOK_APP_ID);
      setSdkStatus('loaded');
      addTestResult('✅ Facebook SDK loaded successfully');
      
      // Check if FB object is available
      if (window.FB) {
        addTestResult('✅ FB object is available');
        
        // Get app info using Graph API (no login required)
        testAppInfo();
        checkLoginStatus();
      } else {
        addTestResult('❌ FB object not found');
      }
    } catch (error) {
      setSdkStatus('error');
      addTestResult(`❌ Error loading SDK: ${error}`);
    }
  };

  const addTestResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testAppInfo = () => {
    // This tests that the SDK is working without requiring login
    try {
      addTestResult('Testing Graph API connection...');
      
      // We can get public app information without login
      window.FB.api(
        `/${FACEBOOK_APP_ID}`,
        { fields: 'name,id' },
        (response: any) => {
          if (response && !response.error) {
            setAppInfo(response);
            addTestResult(`✅ App found: ${response.name || 'App ID verified'}`);
          } else {
            addTestResult(`❌ Graph API error: ${JSON.stringify(response.error)}`);
          }
        }
      );
    } catch (error) {
      addTestResult(`❌ API test error: ${error}`);
    }
  };

  const checkLoginStatus = () => {
    window.FB.getLoginStatus((response: any) => {
      setLoginStatus(response.status);
      addTestResult(`Login status: ${response.status}`);
      
      if (response.status === 'connected') {
        addTestResult('✅ User is logged in and authenticated');
      } else if (response.status === 'not_authorized') {
        addTestResult('⚠️ User is logged into Facebook but not your app');
      } else {
        addTestResult('ℹ️ User is not logged into Facebook');
      }
    });
  };

  const testShare = () => {
    // Test share dialog (doesn't require login)
    window.FB.ui({
      method: 'share',
      href: 'https://biig.dev',
    }, (response: any) => {
      if (response && !response.error_message) {
        addTestResult('✅ Share dialog opened successfully');
      } else {
        addTestResult('❌ Share dialog error');
      }
    });
  };

  const testSend = () => {
    // Test send dialog (Messenger share - doesn't require login)
    window.FB.ui({
      method: 'send',
      link: 'https://biig.dev',
    }, (response: any) => {
      if (response && !response.error_message) {
        addTestResult('✅ Send dialog opened successfully');
      } else {
        addTestResult('ℹ️ Send dialog closed or errored');
      }
    });
  };

  const testPagePlugin = () => {
    // Parse XFBML plugins on the page
    window.FB.XFBML.parse();
    addTestResult('✅ XFBML plugins parsed');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Facebook SDK Test Page</h1>
          
          {/* SDK Status */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">SDK Status</h2>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                sdkStatus === 'loaded' ? 'bg-green-500' :
                sdkStatus === 'error' ? 'bg-red-500' :
                'bg-yellow-500 animate-pulse'
              }`} />
              <span className="font-medium">
                {sdkStatus === 'loaded' ? 'SDK Loaded' :
                 sdkStatus === 'error' ? 'SDK Error' :
                 'Loading SDK...'}
              </span>
            </div>
          </div>

          {/* App Info */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">App Information</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <p><strong>App ID:</strong> {FACEBOOK_APP_ID}</p>
              {appInfo && (
                <>
                  <p><strong>App Name:</strong> {appInfo.name || 'N/A'}</p>
                  <p><strong>Status:</strong> Connected to Graph API ✅</p>
                </>
              )}
              <p><strong>Login Status:</strong> {loginStatus || 'Checking...'}</p>
            </div>
          </div>

          {/* Test Buttons */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">Test Functions (No Login Required)</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={testShare}
                disabled={sdkStatus !== 'loaded'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                Test Share Dialog
              </button>
              <button
                onClick={testSend}
                disabled={sdkStatus !== 'loaded'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                Test Messenger Send
              </button>
              <button
                onClick={testPagePlugin}
                disabled={sdkStatus !== 'loaded'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                Test Page Plugin
              </button>
              <button
                onClick={checkLoginStatus}
                disabled={sdkStatus !== 'loaded'}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                Refresh Status
              </button>
            </div>
          </div>

          {/* Test Results Log */}
          <div>
            <h2 className="text-xl font-semibold mb-3">Test Results</h2>
            <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div key={index} className="mb-1">{result}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Facebook Page Plugin Test */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-xl font-semibold mb-4">Facebook Page Plugin Test</h2>
          <p className="text-gray-600 mb-4">
            This tests if Facebook plugins can load (doesn't require app verification):
          </p>
          
          {/* Like Button */}
          <div className="mb-4">
            <div 
              className="fb-like" 
              data-href="https://biig.dev" 
              data-width=""
              data-layout="standard" 
              data-action="like" 
              data-size="large" 
              data-share="true"
            />
          </div>

          {/* Comments Plugin */}
          <div className="mb-4">
            <div 
              className="fb-comments" 
              data-href="https://biig.dev/facebook-test" 
              data-width="100%" 
              data-numposts="5"
            />
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-6">
          <h3 className="font-semibold text-yellow-900 mb-2">ℹ️ Note on App Verification</h3>
          <p className="text-yellow-800 text-sm">
            Your app (ID: {FACEBOOK_APP_ID}) needs verification for full login functionality. 
            However, you can still:
          </p>
          <ul className="list-disc list-inside text-yellow-800 text-sm mt-2">
            <li>Test SDK initialization</li>
            <li>Use share and send dialogs</li>
            <li>Embed Facebook plugins (like, share, comments)</li>
            <li>Test with your own Facebook account (as app admin)</li>
            <li>Add test users in Facebook App Dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
};